/**
 * DocumentEditor — Full-screen TipTap editor with real-time collaboration
 * Connects to Hocuspocus WebSocket for Yjs CRDT sync
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Highlight } from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Collaboration } from '@tiptap/extension-collaboration';
import { CollaborationCursor } from '@tiptap/extension-collaboration-cursor';
import { HocuspocusProvider } from '@hocuspocus/provider';
import * as Y from 'yjs';

const API = '/api/work/documents';
const COLORS = ['#4f46e5', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#be185d', '#65a30d'];

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

export default function DocumentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [saved, setSaved] = useState(true);
  const [connectedUsers, setConnectedUsers] = useState([]);

  const ydoc = useMemo(() => new Y.Doc(), []);

  const provider = useMemo(() => {
    if (!id) return null;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/collaboration`;
    const sessionToken = getCookie('session_token');

    const p = new HocuspocusProvider({
      url: wsUrl,
      name: `doc:${id}`,
      document: ydoc,
      token: sessionToken || 'none',
      onAuthenticationFailed: () => console.warn('Hocuspocus auth failed'),
      onSynced: () => setSaved(true),
      onAwarenessUpdate: ({ states }) => {
        const users = [];
        states.forEach((state) => { if (state.user) users.push(state.user); });
        setConnectedUsers(users);
      },
    });

    return p;
  }, [id, ydoc]);

  // Load document metadata
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API}/${id}`, { credentials: 'include' });
        const data = await res.json();
        setDoc(data.document);
        setTitle(data.document?.title || 'Untitled');
      } catch {
        navigate(doc?.project_id ? `/work?project=${doc.project_id}` : '/work');
      } finally {
        setLoading(false);
      }
    }
    load();

    return () => {
      provider?.destroy();
      ydoc.destroy();
    };
  }, [id]);

  const userColor = useMemo(() => COLORS[Math.floor(Math.random() * COLORS.length)], []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      Image,
      Placeholder.configure({ placeholder: 'Start writing...' }),
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Color,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Collaboration.configure({ document: ydoc }),
      CollaborationCursor.configure({
        provider,
        user: { name: 'You', color: userColor },
      }),
    ],
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[500px] px-8 py-6 [&_blockquote]:bg-gray-50 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:rounded-r-lg [&_blockquote]:px-6 [&_blockquote]:py-4 [&_blockquote]:my-4 [&_blockquote]:not-italic',
      },
      handleDrop: (view, event) => {
        const files = [...(event.dataTransfer?.files || [])].filter(f => f.type.startsWith('image/'));
        if (files.length === 0) return false;
        event.preventDefault();
        for (const file of files) {
          const form = new FormData();
          form.append('file', file);
          fetch('/api/work/documents/upload-image', { method: 'POST', credentials: 'include', body: form })
            .then(r => r.json())
            .then(data => {
              if (data.storageKey) {
                const src = `/api/work/documents/image/${data.storageKey}`;
                view.dispatch(view.state.tr.replaceSelectionWith(
                  view.state.schema.nodes.image.create({ src })
                ));
              }
            }).catch(() => {});
        }
        return true;
      },
      handlePaste: (view, event) => {
        const items = [...(event.clipboardData?.items || [])];
        const imageItem = items.find(i => i.type.startsWith('image/'));
        if (!imageItem) return false;
        event.preventDefault();
        const file = imageItem.getAsFile();
        if (!file) return false;
        const form = new FormData();
        form.append('file', file);
        fetch('/api/work/documents/upload-image', { method: 'POST', credentials: 'include', body: form })
          .then(r => r.json())
          .then(data => {
            if (data.storageKey) {
              const src = `/api/work/documents/image/${data.storageKey}`;
              view.dispatch(view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src })
              ));
            }
          }).catch(() => {});
        return true;
      },
    },
    onUpdate: () => {
      setSaved(false);
      clearTimeout(window._docSaveTimer);
      window._docSaveTimer = setTimeout(() => setSaved(true), 3000);
    },
  }, [ydoc, provider]);

  // Inject initial content from file conversion (runs once when editor + doc are ready)
  useEffect(() => {
    if (!editor || !doc) return;
    const initialContent = doc.settings?.initialContent;
    if (initialContent && editor.isEmpty) {
      // Small delay to ensure Hocuspocus has synced (if doc is truly empty, inject content)
      const timer = setTimeout(() => {
        if (editor.isEmpty) {
          editor.commands.setContent(initialContent);
          // Clear initialContent from settings so it doesn't re-inject on next load
          fetch(`${API}/${id}`, {
            method: 'PUT', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ settings: {} }),
          }).catch(() => {});
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [editor, doc]);

  async function handleTitleChange(newTitle) {
    setTitle(newTitle);
    try {
      await fetch(`${API}/${id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle }),
      });
    } catch { /* */ }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-white">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-4 py-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button onClick={() => navigate(doc?.project_id ? `/work?project=${doc.project_id}` : '/work')} className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 flex-shrink-0">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="hidden sm:flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 flex-shrink-0">
            {doc?.doc_type === 'spreadsheet' ? (
              <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M10 3v18M14 3v18" /></svg>
            ) : doc?.doc_type === 'presentation' ? (
              <svg className="h-4 w-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
            ) : (
              <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            )}
          </div>
          <input
            type="text" value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="border-none bg-transparent text-base sm:text-lg font-semibold text-gray-900 focus:outline-none focus:ring-0 flex-1 min-w-0"
            placeholder="Untitled"
          />
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <div className="flex -space-x-2">
            {connectedUsers.slice(0, 3).map((user, i) => (
              <div key={i} className="flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white"
                style={{ backgroundColor: user.color || '#4f46e5' }} title={user.name}>
                {(user.name || '?')[0].toUpperCase()}
              </div>
            ))}
          </div>
          <span className={`text-xs hidden sm:inline ${saved ? 'text-gray-400' : 'text-amber-500'}`}>
            {saved ? 'Saved' : 'Saving...'}
          </span>
        </div>
      </header>

      {/* Toolbar */}
      {editor && <Toolbar editor={editor} />}

      {/* Editor */}
      <div className="flex-1 overflow-auto">
        <div className="mx-auto max-w-3xl px-4 sm:px-0 py-4 sm:py-8">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}

function Toolbar({ editor }) {
  if (!editor) return null;

  const btn = (active, onClick, children, title) => (
    <button onClick={onClick}
      className={`rounded p-1.5 ${active ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
      title={title}>
      {children}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-100 px-4 py-1.5">
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), <span className="text-xs font-bold">B</span>, 'Bold')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), <span className="text-xs italic">I</span>, 'Italic')}
      {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), <span className="text-xs underline">U</span>, 'Underline')}
      {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), <span className="text-xs line-through">S</span>, 'Strikethrough')}
      {btn(editor.isActive('highlight'), () => editor.chain().focus().toggleHighlight().run(), <span className="text-xs bg-yellow-200 px-0.5">H</span>, 'Highlight')}
      <div className="mx-1 h-5 w-px bg-gray-200" />
      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), <span className="text-[10px] font-bold">H1</span>, 'Heading 1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <span className="text-[10px] font-bold">H2</span>, 'Heading 2')}
      {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), <span className="text-[10px] font-bold">H3</span>, 'Heading 3')}
      <div className="mx-1 h-5 w-px bg-gray-200" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(),
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>, 'Bullet list')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), <span className="text-[10px] font-mono">1.</span>, 'Numbered list')}
      {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), <span className="text-xs">"</span>, 'Quote')}
      {btn(editor.isActive('codeBlock'), () => editor.chain().focus().toggleCodeBlock().run(), <span className="text-[10px] font-mono">&lt;/&gt;</span>, 'Code block')}
      <div className="mx-1 h-5 w-px bg-gray-200" />
      {btn(editor.isActive({ textAlign: 'left' }), () => editor.chain().focus().setTextAlign('left').run(),
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h16" /></svg>, 'Left')}
      {btn(editor.isActive({ textAlign: 'center' }), () => editor.chain().focus().setTextAlign('center').run(),
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M4 18h16" /></svg>, 'Center')}
      {btn(editor.isActive({ textAlign: 'right' }), () => editor.chain().focus().setTextAlign('right').run(),
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M4 18h16" /></svg>, 'Right')}
      <div className="mx-1 h-5 w-px bg-gray-200" />
      {btn(false, () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M10 3v18M14 3v18" /></svg>, 'Insert table')}
      {btn(editor.isActive('link'), () => {
        const url = prompt('URL:');
        if (url) editor.chain().focus().setLink({ href: url }).run();
      }, <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>, 'Link')}
      {btn(false, () => {
        const url = prompt('Image URL:');
        if (url) editor.chain().focus().setImage({ src: url }).run();
      }, <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>, 'Image')}
      <div className="flex-1" />
      {btn(false, () => editor.chain().focus().undo().run(),
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>, 'Undo')}
      {btn(false, () => editor.chain().focus().redo().run(),
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" /></svg>, 'Redo')}
    </div>
  );
}
